import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { IItemForGetShortDishInfo } from 'types';
import { Checkbox } from 'antd';
import FormSelect from 'components/Select/FormSelect';
import Text from 'components/Text/Text';
import SearchField from 'components/SearchField/SearchField';
import ChunkOfTextWithCopying from 'components/Text/ChunkOfTextWithCopying';
import { ModificationsDishCategorySelect } from './ModificationsDishCategorySelect';
import { ModificationsDishSubCategorySelect } from './ModificationsDishSubCategorySelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './MultipleSelect.module.scss';

interface IProps {
	refetch: () => void;
	setModificationsDishCategory: (value: Nullable<string>) => void;
	setModificationsDishSubCategory: (value: Nullable<string>) => void;
	setModificationsDishes: (value: string[]) => void;
	removeModificationsDish: (value: string) => void;
	data: IItemForGetShortDishInfo[];
	IDsSelectedRestaurant: string[];
	selectedModificationsDishCategory: Nullable<string>;
	selectedModificationsDishSubCategory: Nullable<string>;
	fetchedModificationsDishes: IItemForGetShortDishInfo[];
	selectedModificationsDishes: string[];
	isLoading: boolean;
	responseError: boolean;
}

export const MultipleSelect: React.FC<IProps> = React.memo(
	({
		refetch,
		setModificationsDishCategory,
		setModificationsDishSubCategory,
		setModificationsDishes,
		removeModificationsDish,
		data,
		IDsSelectedRestaurant,
		selectedModificationsDishCategory,
		selectedModificationsDishSubCategory,
		fetchedModificationsDishes,
		selectedModificationsDishes,
		isLoading,
		responseError,
	}) => {
		const [searchString, setSearchString] = useState<string>('');

		const filteredDataWithAdditionalContent = useMemo(() => {
			const updatedData = data.map((el) => ({
				...el,
				additionalContent: (
					<>{el.externalId && <ChunkOfTextWithCopying text={el.externalId} className={style.externalId} />}</>
				),
			}));

			if (searchString) {
				const filteredDataByName = updatedData.filter((el) =>
					el.name.toLowerCase().includes(searchString.toLowerCase()),
				);
				const filteredDataByExternalId = updatedData.filter((el) =>
					el.externalId?.toLowerCase().includes(searchString.toLowerCase()),
				);
				const unionFilteredData = Array.from(new Set([...filteredDataByName, ...filteredDataByExternalId]));
				return unionFilteredData;
			}
			return updatedData;
		}, [searchString, data]);

		const handleSelectAllData = useCallback(() => {
			const isCheckedAllItems = data.every((el) => selectedModificationsDishes.includes(el.id));
			const dataIDs = data.map((el) => el.id);
			if (isCheckedAllItems) {
				setModificationsDishes(selectedModificationsDishes.filter((el) => !dataIDs.includes(el)));
			} else {
				const filteredData = data.map((el) => el.id).filter((el) => !selectedModificationsDishes.includes(el));
				setModificationsDishes([...selectedModificationsDishes, ...filteredData]);
			}
		}, [data, selectedModificationsDishes, setModificationsDishes]);

		useEffect(() => {
			setSearchString('');
		}, [selectedModificationsDishSubCategory]);

		const selectedItemsList = useMemo(() => {
			return selectedModificationsDishes.map((el) => {
				const label = fetchedModificationsDishes?.find((item) => item.id === el)?.name || '';

				return <SelectedItem key={el} label={label} onDelete={() => removeModificationsDish(el)} />;
			});
		}, [selectedModificationsDishes, fetchedModificationsDishes, removeModificationsDish]);

		const renderSelectMenu = useCallback(
			(menu: React.ReactElement) => {
				const isChecked = () => {
					return data.every((el) => selectedModificationsDishes.includes(el.id));
				};

				return (
					<div className={style.container}>
						<Text
							translationNameSpace="form"
							translationKey="fillInRequiredFilters"
							size="s"
							weight="bold"
						/>
						<div className={style.selectMenuHeader}>
							<div className={style.filterSelects}>
								<ModificationsDishCategorySelect
									IDsSelectedRestaurant={IDsSelectedRestaurant}
									setModificationsDishCategory={setModificationsDishCategory}
								/>
								<ModificationsDishSubCategorySelect
									selectedModificationsDishCategory={selectedModificationsDishCategory}
									setModificationsDishSubCategory={setModificationsDishSubCategory}
								/>
							</div>
							<SearchField onChangeSelectSearchString={setSearchString} searchString={searchString} />
							<Checkbox
								className={style.checkBox}
								checked={!!(data.length && isChecked())}
								disabled={!data.length}
								onChange={handleSelectAllData}>
								<Text
									translationNameSpace="form"
									translationKey="selectAllDishesLabel"
									asFragment={true}
								/>
							</Checkbox>
							{!!selectedItemsList.length && (
								<div className={style.selectedItemsList}>{selectedItemsList}</div>
							)}
						</div>
						{menu}
					</div>
				);
			},
			[selectedModificationsDishes, data, handleSelectAllData, setSearchString, searchString],
		);

		return (
			<FormSelect
				name="selectedModificationsDishes"
				label="dishesLabel"
				options={filteredDataWithAdditionalContent}
				fetchError={responseError}
				loading={isLoading}
				disabled={!IDsSelectedRestaurant.length}
				onRefetch={refetch}
				onChange={setModificationsDishes}
				counterValue={selectedModificationsDishes.length}
				dropdownRender={renderSelectMenu}
				mode="multiple"
			/>
		);
	},
);
